import i18n from 'i18n-literally'
import memoizeOne from 'memoize-one'
import { curry } from 'ramda'
import { createSelector } from 'redux-bundler'

import {
  AutoAwesome as ChatIcon,
  BarChart as FacilityPerfIcon,
  Bluetooth as BluetoothIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  ShowChart as RoomDashboardIcon,
} from '@mui/icons-material'

import { fallbackContent } from '~/src/App/utils'
import { urls as harvestUrls } from '~/src/Harvest/bundle'
import { getInventoryPermits } from '~/src/Inventory/utils'
import createRouteBundle from '~/src/Lib/createRouteBundle'
import { capitalizeInitial, EMPTY_ARRAY, lazyWithRetries, shallowEquals } from '~/src/Lib/Utils'
import { urls as recipeUrls } from '~/src/Recipe/bundle'
import FlagAccountCreationWrapper from '~/src/Register/Flag/FlagWrapper'
import { urls as roomUrls } from '~/src/Room/bundle'
import Forgot from '~/src/Routes/Forgot'
import Reset from '~/src/Routes/Forgot/reset'
import Login from '~/src/Routes/Login'
import OAuthAuthorize from '~/src/Routes/OAuth/Authorize'
import { URL_ACTION } from '~/src/Store/constants'
import BatchIcon from '~/src/UI/Icons/Batch'
import JournalIcon from '~/src/UI/Icons/JournalNav'
import PestIcon from '~/src/UI/Icons/Pest'
import Strain from '~/src/UI/Icons/Strain'
import StrainrunsIcon from '~/src/UI/Icons/Strainruns'
import WeatherMoistureIcon from '~/src/UI/Icons/WeatherMoisture'
import RedirectTo from '~/src/UI/Shared/RedirectTo'

import {
  FACILITY_REDIRECT,
  FACILITY_ROUTE_PREFIX,
  FACILITY_SEGMENT_PATTERN,
  PUBLIC_ROUTES,
  REGISTER_ROUTES,
} from './constants'
import Logout from './Logout'
import RedirectToFacilityPrefixed from './RedirectToFacilityPrefixed'

const AccountCreationRegisterGateway = lazyWithRetries(() => import('~/src/Register'))
const AccountCreationSuccess = lazyWithRetries(() => import('~/src/Register/AccountFlow/AccountCreationSuccess'))
const AccountCreationFacilityInfo = lazyWithRetries(() => import('~/src/Register/AccountFlow/FacilityInfo'))
const AccountCreationPassword = lazyWithRetries(() => import('~/src/Register/AccountFlow/Password'))
const AccountCreationPersonalData = lazyWithRetries(() => import('~/src/Register/AccountFlow/PersonalData'))
const AccountCreationGatewaySuccess = lazyWithRetries(() => import('~/src/Register/GatewaySuccess'))
const AccountCreationError = lazyWithRetries(() => import('~/src/Register/AccountFlow/AccountCreationError'))
const AccountProfile = lazyWithRetries(() => import('~/src/Account/Profile'))
const Account = lazyWithRetries(() => import('~/src/Account/Management'))
const Chat = lazyWithRetries(() => import('~/src/Chat'))
const Cultivars = lazyWithRetries(() => import('~/src/Cultivars'))
const CultivarSpecification = lazyWithRetries(() => import('~/src/Cultivars/Specification'))
const DevicesQR = lazyWithRetries(() => import('~/src/Device/qr'))
const FacilityDashboard = lazyWithRetries(() => import('~/src/Facility/Routes/Dashboard'))
const FacilityIrrigation = lazyWithRetries(() => import('~/src/Facility/Irrigation'))
const HarvestDetail = lazyWithRetries(() => import('~/src/Harvest/Detail'))
const HarvestCreate = lazyWithRetries(() => import('~/src/Harvest/CreateNonMetrc'))
const IPMList = lazyWithRetries(() => import('~/src/IPM/Routes'))
const Journal = lazyWithRetries(() => import('~/src/Journal/Routes/JournalWithIrrigation'))
const IPMPesticideList = lazyWithRetries(() => import('~/src/IPM/Routes/PesticideList'))
const FacilityPerformance = lazyWithRetries(() => import('~/src/Performance'))
const Production = lazyWithRetries(() => import('~/src/Production'))
const RecipeDetail = lazyWithRetries(() => import('~/src/Recipe/Detail'))
const RoomDashboard = lazyWithRetries(() => import('~/src/Room/Routes/Dashboard'))
const RoomDashboardRedirect = lazyWithRetries(() =>
  import('~/src/Room/Routes/Dashboard').then(({ RedirectRoute }) => ({ default: RedirectRoute })),
)
const NotFound = lazyWithRetries(() => import('~/src/Routes/NotFound'))
const Setup = lazyWithRetries(() => import('~/src/Setup'))
const Solus = lazyWithRetries(() => import('~/src/Native/Solus'))
const StaffManagement = lazyWithRetries(() => import('~/src/Staff/Routes/Manage'))
const StrainrunAnalytics = lazyWithRetries(() => import('~/src/Strainrun/Analytics'))

export const roomRoute = '/rooms/:id'
const memoizeOneShallowEquals = (left, right) => shallowEquals(left, right, 3)

const PRIVATE_ROUTE_REDIRECT = {
  component: RedirectToFacilityPrefixed,
  [FACILITY_REDIRECT]: true,
}

const ROOM_DASHBOARD_ROUTE_CONFIG = {
  component: RoomDashboard,
  actions: 'view_room',
}

const GROWLOG_ROUTE_CONFIG = {
  component: HarvestDetail,
  title: i18n`Harvest Growlog`,
  actions: ['view_harvest'],
  facilityTypes: ['CEA', 'CORE'],
}

const CULTIVARS_ROUTE_CONFIG = {
  component: Cultivars,
  actions: 'view_cultivar_profiles',
  title: i18n`Cultivars`,
  facilityTypes: 'CORE',
}

const CULTIVAR_PROFILE_ROUTE_CONFIG = {
  component: CultivarSpecification,
  actions: 'view_cultivar_profiles',
  facilityTypes: 'CORE',
  title: i18n`Cultivar Profile`,
  PermissionFallback: NotFound,
}

const PROFILE_ROUTE_CONFIG = {
  component: AccountProfile,
  title: i18n`Your Profile`,
}

const routeBundle = createRouteBundle(
  Object.entries({
    // This route is a special case that will be used to redirect to the dashboard or login page
    '/': {
      component: () => fallbackContent,
      publicAccess: true,
      viewMode: 'fullscreen',
    },
    '/dashboard': {
      component: FacilityDashboard,
      title: i18n`Home`,
      nav: {
        Icon: DashboardIcon,
        sequence: 1,
      },
    },
    '/chat': {
      component: Chat,
      title: i18n`Assistant`,
      features: 'LLM_CHAT',
      noScroll: true,
      viewMode: 'chat',
      nav: {
        Icon: ChatIcon,
        mobile: false,
        sequence: 12,
      },
    },
    [PUBLIC_ROUTES.LOGIN]: {
      component: Login,
      publicAccess: true,
      title: i18n`Login`,
      viewMode: 'fullscreen',
    },
    [PUBLIC_ROUTES.LOGOUT]: Logout,
    [PUBLIC_ROUTES.OAUTH_AUTHORIZE]: {
      component: OAuthAuthorize,
      publicAccess: true,
      title: i18n`Authorize AROYA`,
      viewMode: 'fullscreen',
    },
    [PUBLIC_ROUTES.FORGOT]: {
      component: Forgot,
      publicAccess: true,
      title: i18n`Forgot Password?`,
      viewMode: 'fullscreen',
    },
    [`${PUBLIC_ROUTES.FORGOT}/:email`]: {
      component: Forgot,
      publicAccess: true,
      title: i18n`Forgot Password?`,
      viewMode: 'fullscreen',
    },
    [`${PUBLIC_ROUTES.RESET}/:token`]: {
      component: Reset,
      publicAccess: true,
      viewMode: 'fullscreen',
    },
    [PUBLIC_ROUTES.SWITCHING]: {
      additionalProps: {
        switching: true,
      },
      component: Login,
      publicAccess: true,
      title: i18n`Switching facilties`,
      viewMode: 'fullscreen',
    },
    [`${REGISTER_ROUTES.GATEWAY}`]: {
      component: FlagAccountCreationWrapper,
      additionalProps: {
        handler: AccountCreationRegisterGateway,
        requiredToken: false,
      },
      publicAccess: true,
      title: i18n`Register gateway`,
    },
    [`${REGISTER_ROUTES.GATEWAY_SUCCESS}`]: {
      component: FlagAccountCreationWrapper,
      additionalProps: {
        handler: AccountCreationGatewaySuccess,
        requiredToken: true,
      },
      publicAccess: true,
      title: i18n`Success`,
    },
    [`${REGISTER_ROUTES.NEW_ACCOUNT}`]: {
      component: FlagAccountCreationWrapper,
      additionalProps: {
        handler: AccountCreationPersonalData,
        requiredToken: true,
      },
      publicAccess: true,
      title: i18n`New Account`,
    },
    [`${REGISTER_ROUTES.FACILITY_INFO}`]: {
      component: FlagAccountCreationWrapper,
      additionalProps: {
        handler: AccountCreationFacilityInfo,
        requiredToken: false,
      },
      publicAccess: true,
      title: i18n`New Account`,
    },
    [`${REGISTER_ROUTES.PASSWORD}`]: {
      component: FlagAccountCreationWrapper,
      additionalProps: {
        handler: AccountCreationPassword,
        requiredToken: true,
      },
      publicAccess: true,
      title: i18n`New Account`,
    },
    [`${REGISTER_ROUTES.ACCOUNT_SUCCESS}`]: {
      component: FlagAccountCreationWrapper,
      additionalProps: {
        handler: AccountCreationSuccess,
        requiredToken: true,
      },
      publicAccess: true,
      title: i18n`Success`,
    },
    [`${REGISTER_ROUTES.ACCOUNT_ERROR}`]: {
      component: FlagAccountCreationWrapper,
      additionalProps: {
        handler: AccountCreationError,
        requiredToken: false,
      },
      publicAccess: true,
      title: i18n`Success`,
    },
    '/setup': {
      component: Setup,
      title: i18n`Settings`,
      actions: ['change_facility', 'change_staff', 'view_device', 'change_compliance'],
      anyAction: true,
      nav: {
        Icon: SettingsIcon,
        sequence: 11,
      },
    },
    '/solus': {
      component: Solus,
      get title() {
        return i18n`SOLUS`
      },
      actions: 'change_manualreading',
      nav: {
        Icon: BluetoothIcon,
        mobile: false,
        nativeOnly: true,
        sequence: 4,
      },
    },
    '/setup/inventory': {
      component: Setup,
      title: i18n`Setup - Inventory`,
      actions: 'change_compliance',
    },
    '/setup/devices': {
      component: Setup,
      title: i18n`Setup - Devices`,
      actions: 'view_device',
    },
    '/setup/controllers': {
      component: Setup,
      title: i18n`Setup - Irrigation Controllers`,
      actions: 'view_irrigation',
    },
    '/setup/facility': {
      component: Setup,
      title: i18n`Setup - Facility`,
      actions: 'change_facility',
    },
    '/setup/staff': {
      component: Setup,
      title: i18n`Setup - Staff`,
      actions: 'change_staff',
    },
    '/rooms': {
      component: RoomDashboardRedirect,
      actions: 'view_room',
      nav: {
        Icon: RoomDashboardIcon,
        label: ({ facilityLocationsTerm }) => capitalizeInitial(facilityLocationsTerm(EMPTY_ARRAY, 'room')),
        sequence: 2,
        getURL: memoizeOne(({ currentFacilityId, rooms = [] }) => {
          if (currentFacilityId && rooms.length) {
            const [id] = rooms
            return `/f/${currentFacilityId}${roomUrls.getUrl({ id })}`
          }
          return roomUrls.list
        }, memoizeOneShallowEquals),
        getUrlList: memoizeOne(({ currentFacilityId, rooms = [] }) => {
          if (currentFacilityId && rooms.length) {
            return rooms.flatMap(id => [
              `/f/${currentFacilityId}${roomUrls.getUrl({ id })}`,
              `/f/${currentFacilityId}${roomUrls.getUrl({ id, action: 'dashboard' })}`,
            ])
          }
          return EMPTY_ARRAY
        }, memoizeOneShallowEquals),
      },
    },
    '/rooms/:id': ROOM_DASHBOARD_ROUTE_CONFIG,
    '/rooms/:id/dashboard': ROOM_DASHBOARD_ROUTE_CONFIG,
    // '/r/:id/:slug': RoomDashboard,
    '/profile': {
      ...PROFILE_ROUTE_CONFIG,
      urlTest: url => url.match(/^(\/f\/d+)?\/profile/),
    },
    '/profile/:tab': PROFILE_ROUTE_CONFIG,
    '/account': {
      component: Account,
      title: i18n`Your Account`,
    },
    '/irrigation': {
      component: FacilityIrrigation,
      title: i18n`Irrigation`,
      actions: 'view_irrigation',
      features: 'IRRIGAIT',
      nav: {
        Icon: WeatherMoistureIcon,
        mobile: false,
        sequence: 3,
      },
    },
    '/journal': {
      component: Journal,
      title: i18n`Journal`,
      nav: {
        Icon: JournalIcon,
        mobile: false,
        sequence: 5,
      },
    },
    '/qr/:sn': {
      component: DevicesQR,
    },
    '/staff/manage': {
      component: StaffManagement,
      title: i18n`Manage Staff`,
      actions: 'change_staff',
      facilityTypes: ['CEA', 'CORE'],
    },
    '/staff/:id': {
      component: AccountProfile,
      actions: 'change_staff',
    },
    '/staff/:id/:tab': {
      component: AccountProfile,
      actions: 'change_staff',
    },
    '/ipm/pesticides': {
      component: IPMPesticideList,
      title: i18n`IPM Pesticides`,
      actions: 'change_pesticide_application',
      facilityTypes: 'CORE',
    },
    '/ipm': {
      component: IPMList,
      title: i18n`IPM`,
      actions: 'view_pesticide_application',
      facilityTypes: 'CORE',
      nav: {
        Icon: PestIcon,
        mobile: false,
        sequence: 6,
      },
    },
    '/production': {
      component: Production,
      title: i18n`Production`,
      actions: ['view_harvest', 'view_recipe', 'view_package', 'view_plant'],
      anyAction: true,
      facilityTypes: ['CEA', 'CORE'],
      nav: {
        Icon: BatchIcon,
        labelColor: 'secondary',
        sequence: 7,
        getUrlList: memoizeOne(
          ({ currentFacilityId }) =>
            ['/harvests', '/recipes', '/inventory', '/irrigationTemplates'].map(
              pattern => `/f/${currentFacilityId}${pattern}`,
            ),
          memoizeOneShallowEquals,
        ),
        facilityTypes: ['CEA', 'CORE'],
      },
    },
    '/irrigationSchedules': {
      component: RedirectTo,
      additionalProps: { url: '/irrigationTemplates' },
    },
    '/irrigationTemplates': {
      component: Production,
      title: i18n`Irrigation schedules`,
      actions: ['view_irrigation'],
    },
    [recipeUrls.list]: {
      component: Production,
      title: i18n`Recipes`,
      actions: 'view_recipe',
    },
    [recipeUrls.create]: {
      component: RecipeDetail,
      title: i18n`Create Recipe`,
      actions: 'change_recipe',
    },
    [recipeUrls.view]: {
      component: RecipeDetail,
      actions: 'view_recipe',
    },
    [harvestUrls.list]: {
      component: Production,
      title: i18n`Harvests`,
      actions: 'view_harvest',
      facilityTypes: ['CEA', 'CORE'],
    },
    [harvestUrls.cultivars]: {
      component: HarvestDetail,
      title: i18n`Cultivars`,
      permissions: 'view_harvest',
    },
    [harvestUrls.create]: {
      component: HarvestCreate,
      title: i18n`Create Harvest`,
      actions: 'change_harvest',
      facilityTypes: ['CEA', 'CORE'],
    },
    [harvestUrls.view]: GROWLOG_ROUTE_CONFIG,
    [harvestUrls.compare]: {
      component: HarvestDetail,
      title: i18n`Run Comparison`,
      actions: ['view_harvest'],
      features: ['RUN_COMPARISON'],
      facilityTypes: ['CEA', 'CORE'],
    },
    [harvestUrls.schedule]: {
      component: HarvestDetail,
      title: i18n`Harvest Schedule`,
      actions: 'view_harvest',
      facilityTypes: ['CEA', 'CORE'],
    },
    [harvestUrls.yield]: {
      component: HarvestDetail,
      title: i18n`Harvest Yield`,
      actions: 'view_harvest',
      facilityTypes: ['CEA', 'CORE'],
    },
    [harvestUrls.growlog]: GROWLOG_ROUTE_CONFIG,
    [harvestUrls.analytics]: {
      component: HarvestDetail,
      title: i18n`Harvest Analytics`,
      actions: 'view_harvest',
      facilityTypes: ['CEA', 'CORE'],
    },
    [harvestUrls.inventory]: {
      component: HarvestDetail,
      title: i18n`Harvest Inventory`,
      actions: ['view_harvest', getInventoryPermits],
      facilityTypes: ['CEA', 'CORE'],
    },
    '/runs': {
      title: i18n`Runs`,
      component: StrainrunAnalytics,
      facilityTypes: 'CORE',
      nav: {
        Icon: StrainrunsIcon,
        mobile: false,
        sequence: 8,
      },
    },
    '/facility/performance': {
      component: FacilityPerformance,
      title: i18n`Analytics`,
      actions: 'view_performance',
      facilityTypes: 'CORE',
      nav: {
        Icon: FacilityPerfIcon,
        mobile: false,
        sequence: 10,
      },
    },
    '/cultivars': {
      ...CULTIVARS_ROUTE_CONFIG,
      nav: {
        Icon: Strain,
        labelColor: 'secondary',
        mobile: false,
        sequence: 9,
      },
      urlTest: url => url.match(/(\/f\/d+)?\/cultivars/),
    },
    '/cultivars/performance': CULTIVARS_ROUTE_CONFIG,
    '/cultivars/:id': CULTIVAR_PROFILE_ROUTE_CONFIG,
    '/cultivars/:id/:view': CULTIVAR_PROFILE_ROUTE_CONFIG,
    '/cultivars/:id/:view/:tab': CULTIVAR_PROFILE_ROUTE_CONFIG,
    // This route cannot be part of PUBLIC_ROUTES because PUBLIC_ROUTES must be able to match
    // a finite set of routes, but this pattern matches all routes
    '*': {
      component: NotFound,
      publicAccess: true,
      is404: true,
      title: i18n`Unable to locate resource`,
    },
  }).reduce((routes, [path, config]) => {
    config.pattern = path
    if (config.publicAccess) {
      routes[path] = config
    } else {
      routes[path] = PRIVATE_ROUTE_REDIRECT
      routes[`${FACILITY_ROUTE_PREFIX}${path}`] = config
    }
    return routes
  }, {}),
)

routeBundle.getMiddleware = () =>
  curry((store, next, action) => {
    if (action.type !== URL_ACTION) {
      return next(action)
    }
    const { url, ...payload } = action.payload
    const path = url.replace(location.origin, '')
    const { facility } = store.selectRouteParams()
    const routeInfo = store.selectRouteMatcher()(path)
    const { params: nextParams, pattern, value: route } = routeInfo
    if (pattern && pattern.startsWith(FACILITY_ROUTE_PREFIX)) {
      if (nextParams.facility && facility !== nextParams.facility) {
        return store.doFacilitySwitch({
          facility: Number(nextParams.facility),
          url: path,
        })
      }
    }
    if (route && route[FACILITY_REDIRECT]) {
      const nextUrl = `${location.origin}/f/${store.selectCurrentFacilityId()}${path}`

      return next({
        type: URL_ACTION,
        payload: {
          ...payload,
          url: nextUrl,
        },
      })
    }
    return next(action)
  })

routeBundle.selectRouteInfo = createSelector('selectRouteMatcher', 'selectPathname', (routeMatcher, pathname) => {
  const routeInfo = routeMatcher(pathname)
  if (!routeInfo.pattern || !routeInfo.pattern.startsWith(FACILITY_ROUTE_PREFIX)) {
    return routeInfo
  }
  const { pattern } = routeInfo

  const [, id = null] = pathname.match(FACILITY_SEGMENT_PATTERN) ?? []
  const routePattern = pattern.replace(FACILITY_ROUTE_PREFIX, '')

  return {
    ...routeInfo,
    params: { ...routeInfo.params, facility: id },
    pattern: routePattern,
    url: pathname,
  }
})

export default routeBundle
